import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { T, UI, FONT, BTN } from "../constants/tokens";
import { useIsMobile } from "../utils/hooks";
import { modalScrim, modalPanel } from "./Modal";
import Icon from "./Icon";
import LTR from "./LTR";

// ─── A date field you can jump around in ────────────────────────────────────
// The browser's <input type="date"> pages one month at a time; a purchase
// three years back is thirty-odd taps. This field opens a small picker with
// the YEAR and the MONTH as two native <select>s (a wheel on the phone, a
// list on the desktop) and the days as a grid under them. Same contract as
// the native input — `value` and `onChange(value)` are "YYYY-MM-DD" or "" —
// so the callers did not change shape.
//
// Own component, no library: two selects and a 6×7 grid is less code than
// configuring a date library to look like the app, and it consumes tokens
// directly. Future dates are disabled — a purchase date cannot be tomorrow.
//
// Rendered through a portal (sheet on mobile, dialog on desktop), like every
// other overlay here: the mobile carousel track is a transform, and a fixed
// popover inside it lands off-screen.

const MONTHS_HE = ["ינואר", "פברואר", "מרץ", "אפריל", "מאי", "יוני", "יולי", "אוגוסט", "ספטמבר", "אוקטובר", "נובמבר", "דצמבר"];
const WEEK_HE   = ["א", "ב", "ג", "ד", "ה", "ו", "ש"];
const FIRST_YEAR = 1990;

const pad = (n) => String(n).padStart(2, "0");
const iso = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

function parseIso(v) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v || "");
  if (!m) return null;
  return { y: +m[1], m: +m[2] - 1, d: +m[3] };
}

// dd.mm.yyyy, the app's numeric date form.
export function displayDate(v) {
  const p = parseIso(v);
  return p ? `${pad(p.d)}.${pad(p.m + 1)}.${p.y}` : "";
}

function Picker({ value, onPick, onClose }) {
  const isMobile = useIsMobile();
  const today = new Date();
  const todayIso = iso(today.getFullYear(), today.getMonth(), today.getDate());
  const start = parseIso(value) || { y: today.getFullYear(), m: today.getMonth(), d: null };
  const [y, setY] = useState(start.y);
  const [m, setM] = useState(start.m);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
  }, [onClose]);

  const thisYear = today.getFullYear();
  const years = useMemo(() => {
    const out = [];
    for (let yy = thisYear; yy >= FIRST_YEAR; yy -= 1) out.push(yy);
    return out;
  }, [thisYear]);

  // Grid: leading blanks for the weekday the month starts on (Sunday first).
  const firstDow = new Date(y, m, 1).getDay();
  const daysIn   = new Date(y, m + 1, 0).getDate();
  const cells = [...Array(firstDow).fill(null), ...Array.from({ length: daysIn }, (_, i) => i + 1)];

  const selectStyle = {
    flex: 1, minWidth: 0, minHeight: 44, padding: "8px 10px",
    background: UI.surface.page, color: UI.text.primary,
    border: `1px solid ${UI.line.hairline}`, borderRadius: T.radius.md,
    fontSize: 15, fontFamily: "inherit", fontWeight: 600,
  };

  const dayBtn = (d) => {
    const v = iso(y, m, d);
    const future = v > todayIso;
    const selected = v === value;
    const isToday = v === todayIso;
    return (
      <button key={d} disabled={future} onClick={() => onPick(v)}
        aria-label={displayDate(v)} aria-pressed={selected}
        style={{
          minHeight: 40, borderRadius: T.radius.md, fontFamily: "inherit",
          fontSize: 14, fontWeight: selected ? 800 : 600, cursor: future ? "default" : "pointer",
          background: selected ? UI.state.surface : "transparent",
          color: future ? UI.text.muted : selected ? UI.state.fg : UI.text.primary,
          border: `1px solid ${selected ? UI.state.line : isToday ? UI.line.strong : "transparent"}`,
          opacity: future ? 0.4 : 1,
        }}>
        <LTR>{d}</LTR>
      </button>
    );
  };

  return createPortal(
    <div dir="rtl" onClick={onClose} role="dialog" aria-modal="true" aria-label="בחירת תאריך"
      style={modalScrim(1200, { align: isMobile ? "bottom" : "center", padding: isMobile ? 0 : 16 })}>
      <div onClick={e => e.stopPropagation()}
        style={{ ...modalPanel({ variant: isMobile ? "sheet" : "dialog", width: isMobile ? 9999 : 380 }), display: "flex", flexDirection: "column", gap: 12 }}>

        {/* Year and month — the jump the native picker does not have. */}
        <div style={{ display: "flex", gap: 8 }}>
          <select value={m} onChange={e => setM(+e.target.value)} aria-label="חודש" style={selectStyle}>
            {MONTHS_HE.map((name, i) => <option key={name} value={i}>{name}</option>)}
          </select>
          <select value={y} onChange={e => setY(+e.target.value)} aria-label="שנה" style={{ ...selectStyle, flex: "0 0 110px" }}>
            {years.map(yy => <option key={yy} value={yy}>{yy}</option>)}
          </select>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
          {WEEK_HE.map(w => (
            <div key={w} style={{ textAlign: "center", fontSize: 12, fontWeight: 700, color: UI.text.muted, padding: "4px 0" }}>{w}</div>
          ))}
          {cells.map((d, i) => d === null ? <div key={`b${i}`}/> : dayBtn(d))}
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "space-between", paddingTop: 4, borderTop: `1px solid ${UI.line.hairline}` }}>
          <button onClick={() => onPick(todayIso)} style={{ ...BTN.secondary, padding: "8px 14px", minHeight: 40, fontSize: 14 }}>היום</button>
          <div style={{ display: "flex", gap: 8 }}>
            {value && <button onClick={() => onPick("")} style={{ ...BTN.secondary, padding: "8px 14px", minHeight: 40, fontSize: 14 }}>נקה</button>}
            <button onClick={onClose} style={{ ...BTN.secondary, padding: "8px 14px", minHeight: 40, fontSize: 14 }}>סגור</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function DateField({ value = "", onChange, placeholder = "בחר תאריך", style, "aria-label": ariaLabel = "תאריך" }) {
  const [open, setOpen] = useState(false);
  const shown = displayDate(value);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} aria-label={ariaLabel} aria-haspopup="dialog"
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
          width: "100%", minHeight: 44, textAlign: "start", cursor: "pointer", fontFamily: "inherit",
          background: UI.surface.page, border: `1px solid ${UI.line.hairline}`, borderRadius: 6,
          padding: "10px 12px", fontSize: 16, color: shown ? UI.text.primary : UI.text.muted,
          ...style,
        }}>
        <span>{shown ? <LTR style={{ fontFamily: FONT.latin }}>{shown}</LTR> : placeholder}</span>
        <Icon name="calendar" size={16} color={UI.text.muted}/>
      </button>
      {open && (
        <Picker value={value} onPick={(v) => { onChange?.(v); setOpen(false); }} onClose={() => setOpen(false)}/>
      )}
    </>
  );
}
